// EN-5232: continuous sensors (Dexcom CGM, one reading every 5 minutes) send tens of
// thousands of points per range, far more than the chart has pixels. Recharts renders each
// point as its own SVG element, which froze the modal. Charts draw a thinned copy; the
// table and any statistics keep using the full data.
export const MAX_CHART_POINTS_PER_SERIES = 500;

/**
 * Keeps the lowest and highest point of each bucket, in time order, so spikes and dips
 * (hypos and hypers for glucose) survive the thinning. Series at or under `maxPoints`
 * are returned untouched, so sparse measurement types are unaffected.
 */
export function downsampleSeries<T extends Record<string, any>>(
  points: T[],
  valueKey: string,
  maxPoints = MAX_CHART_POINTS_PER_SERIES
): T[] {
  if (points.length <= maxPoints) return points;

  const bucketSize = Math.ceil(points.length / Math.floor(maxPoints / 2));
  const result: T[] = [];

  for (let start = 0; start < points.length; start += bucketSize) {
    const end = Math.min(start + bucketSize, points.length);
    let minIndex = start;
    let maxIndex = start;

    for (let i = start + 1; i < end; i += 1) {
      if (points[i][valueKey] < points[minIndex][valueKey]) minIndex = i;
      if (points[i][valueKey] > points[maxIndex][valueKey]) maxIndex = i;
    }

    if (minIndex === maxIndex) {
      result.push(points[minIndex]);
    } else {
      result.push(
        points[Math.min(minIndex, maxIndex)],
        points[Math.max(minIndex, maxIndex)]
      );
    }
  }

  return result;
}

// Only continuous sensors: thinning blood pressure would unpair systolic/diastolic, and
// thinning spirometry could drop the selected test's point.
const DOWNSAMPLED_TYPES = ["continuous_blood_glucose"];

/** Chart data is keyed `${measurementType}|${source}`; the type is also the value key. */
export function downsampleChartData<T extends Record<string, any>>(
  data: Map<string, T[]>
): Map<string, T[]> {
  const result = new Map<string, T[]>();

  data.forEach((points, key) => {
    const type = key.split("|")[0];
    result.set(
      key,
      DOWNSAMPLED_TYPES.includes(type) ? downsampleSeries(points, type) : points
    );
  });

  return result;
}
